import { config } from '../../config';

// URN transformation functions with specific prefix
function createUrnTransformers(urnPrefix?: string) {
  if (!urnPrefix) {
    // No transformation if prefix is not defined
    return {
      toUrn: (value: string) => value,
      fromUrn: (value: string) => value,
      isUrn: () => false,
    };
  }

  const URN_ID_REGEX =
    /^(?!urn:ngsi-ld)[A-Za-z][a-zA-Z0-9]*(?::[A-Za-z][a-zA-Z0-9]*)*:\S+$/;

  return {
    isUrn: (value: string): boolean => value.startsWith(`${urnPrefix}:`),
    toUrn: (value: string): string => {
      if (value.startsWith(`${urnPrefix}:`)) return value;
      if (URN_ID_REGEX.test(value)) {
        return `${urnPrefix}:${value}`;
      }
      return value;
    },
    fromUrn: (value: string): string =>
      value.startsWith(`${urnPrefix}:`)
        ? value.replace(`${urnPrefix}:`, '')
        : value,
  };
}

/**
 * Polia s voľným textom, ktoré sa **nikdy** neprekladajú na URN.
 *
 * `toUrn` prefixuje každý string, ktorý matchne `^[A-Z][a-zA-Z0-9]*(:…)*:\S+$`,
 * teda aj celý text bez medzery ako `Pozor:Zmena`. Pri prijatom obsahu by
 * `fromUrn` prefix zase odstránil, takže by sa to skrylo - ale pri odoslaní by
 * do databázy šiel poškodený text, ktorý potom vidí každý ďalší konzument.
 *
 * Dnes je to `text` v správach platformy a v šablónach súhlasov, v oboch
 * prípadoch próza v markdowne. Identifikátory sú vždy v poliach `*_id`.
 * Rovnakú výnimku má aj cimpo.
 */
const FREE_TEXT_KEYS = new Set([
  'text',
  // Mená modelov a verzie promptov v prehľade rozhodnutí AI. `llava:7b-v1.6`
  // tvarom vyzerá ako appkové ID (`slovo:niečo`), takže by z filtra odišlo
  // ako URN a nenašlo by nič - staré behy sú presne takto pomenované.
  'model',
  'promptVersion',
]);

function createTransformInput(urnPrefix?: string) {
  const { toUrn } = createUrnTransformers(urnPrefix);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function transformInput(obj: any): any {
    if (obj instanceof File || obj instanceof Blob) return obj; // ✅ zachovať pôvodný objekt
    if (typeof obj === 'string') return toUrn(obj);
    if (Array.isArray(obj)) return obj.map(transformInput);
    if (obj && typeof obj === 'object') {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const out: Record<string, any> = {};
      for (const key in obj) {
        out[key] = FREE_TEXT_KEYS.has(key)
          ? obj[key]
          : transformInput(obj[key]);
      }
      return out;
    }
    return obj;
  }

  return transformInput;
}

function createTransformOutput(urnPrefix?: string) {
  const { fromUrn } = createUrnTransformers(urnPrefix);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function transformOutput(obj: any): any {
    /**
     * **Binárna odpoveď musí prejsť nedotknutá.** Generovaný klient vracia pri
     * inom `Content-Type` než `application/json` rovno `Response` - tak sa
     * sťahuje PDF reportu hlasovania (`adminDownloadVotingReport`).
     *
     * Bez tejto poistky spadne `Response` do vetvy pre objekty nižšie a `for…in`
     * z neho vyrobí **prázdny `{}`**: jeho vlastnosti sú getery na prototype
     * a teda neenumerovateľné. Volajúcemu potom `response.blob()` padne na
     * „is not a function" a vyzerá to ako chyba servera, hoci odpoveď prišla
     * v poriadku. Presne na to sa prišlo pri prvom stiahnutí reportu.
     *
     * `transformInput` má tú istú poistku pre `File` a `Blob` od začiatku -
     * toto je jej chýbajúca polovica na výstupe.
     */
    if (
      obj instanceof Response ||
      obj instanceof Blob ||
      obj instanceof ArrayBuffer
    ) {
      return obj;
    }
    if (typeof obj === 'string') return fromUrn(obj);
    if (Array.isArray(obj)) return obj.map(transformOutput);
    if (obj && typeof obj === 'object') {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const out: Record<string, any> = {};
      for (const key in obj) {
        out[key] = FREE_TEXT_KEYS.has(key)
          ? obj[key]
          : transformOutput(obj[key]);
      }
      return out;
    }
    return obj;
  }

  return transformOutput;
}

export function urnApiWrapper<T extends object>(
  apiInstance: T,
  apiName?: keyof typeof config.API_URN_PREFIXES,
): T {
  const urnPrefix = apiName ? config.API_URN_PREFIXES[apiName] : undefined;
  const transformInput = createTransformInput(urnPrefix);
  const transformOutput = createTransformOutput(urnPrefix);

  return new Proxy(apiInstance, {
    get(target, propKey) {
      const origFn = target[propKey as keyof T];
      if (typeof origFn !== 'function') return origFn;

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return async (...args: any[]) => {
        const transformedArgs = args.map(transformInput);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const result = await (origFn as any).apply(target, transformedArgs);
        return transformOutput(result);
      };
    },
  });
}
